"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.withPromise = exports.withStore = exports.withComponentParam = exports.withComponent = exports.sourceErrorTemplate = exports.connectToState = exports.createConverter = void 0;
const tslib_1 = require("tslib");
const isString_1 = tslib_1.__importDefault(require("lodash/isString"));
const isFunction_1 = tslib_1.__importDefault(require("lodash/isFunction"));
const isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
const curry_1 = tslib_1.__importDefault(require("lodash/curry"));
const some_1 = tslib_1.__importDefault(require("lodash/some"));
const every_1 = tslib_1.__importDefault(require("lodash/every"));
const flattenDeep_1 = tslib_1.__importDefault(require("lodash/flattenDeep"));
const isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
const map_1 = tslib_1.__importDefault(require("lodash/map"));
const get_1 = tslib_1.__importDefault(require("lodash/get"));
const utils_1 = require("../aggregation/utils");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
function hasNecessaryProps(name, convert, test) {
    return (0, isString_1.default)(name) && (0, isFunction_1.default)(convert) && (0, isFunction_1.default)(test);
}
function someNotObservables(observables) {
    return (0, some_1.default)(observables, (o) => !(0, rxjs_1.isObservable)(o));
}
function warn(warning) {
    if (!(0, utils_1.isProduction)()) {
        // eslint-disable-next-line no-console
        console.warn(warning);
    }
}
function createObservableList(convertersOrObservables, sourcesOfState) {
    return (0, flattenDeep_1.default)(convertersOrObservables.map((o) => {
        return (0, rxjs_1.isObservable)(o) ? o : o(sourcesOfState);
    })).filter(rxjs_1.isObservable);
}
function createConverter({ name, convert, test } = {}) {
    if (!hasNecessaryProps(name, convert, test)) {
        throw new Error('ParamStream converter should be in the form {name: String, convert: Function, test: Function}');
    }
    return (0, curry_1.default)((nameSource, stateSource) => {
        const passes = test(nameSource, stateSource);
        if ((0, isString_1.default)(passes) || passes === false) {
            throw new Error(passes ||
                `The arguments and/or the sources of state you provided were refused by the converter ${name}. Please make sure they are correct!`);
        }
        const observables = [].concat(convert(nameSource, stateSource));
        if (someNotObservables(observables)) {
            throw new Error(`Converter ${name} failed to convert some values into Observables`);
        }
        return observables;
    });
}
exports.createConverter = createConverter;
// connectWithStateChanges
function connectToState(...convertersOrObservables) {
    return (sourcesOfState) => {
        const observables = createObservableList(convertersOrObservables, sourcesOfState);
        if ((0, isEmpty_1.default)(observables)) {
            warn('ParamStream will not emit because it was given no converters or observables.');
            return (0, rxjs_1.empty)();
        }
        return (0, rxjs_1.combineLatest)(...observables).pipe((0, operators_1.startWith)(undefined), (0, operators_1.pairwise)(), (0, operators_1.filter)(([previousParams, currentParams]) => {
            if (previousParams === undefined)
                return true;
            const isDuplicate = (0, isEqual_1.default)(previousParams, currentParams);
            if (isDuplicate) {
                warn('Warning: ParamStream emitted the same set of parameters twice. You may be at risk of sending off redundant requests. Ensure Array/Object parameters are not unnecessarily being reinstantiated.');
            }
            return !isDuplicate;
        }), (0, operators_1.map)(([, params]) => (0, flattenDeep_1.default)(params)), (0, operators_1.filter)((params) => {
            return (0, every_1.default)(params, (param) => param.value !== undefined);
        }), (0, operators_1.map)((params) => {
            return params.reduce((map, param, i) => {
                const { name, value } = param;
                if (!name)
                    throw new Error(`Parameter ${i} requires a 'name': ${JSON.stringify(param)}`);
                map[name] = value;
                return map;
            }, {});
        }));
    };
}
exports.connectToState = connectToState;
function isVueComponent(obj) {
    return obj._isVue;
}
function isStore(obj) {
    return obj && obj.watch && obj.commit && obj.dispatch;
}
function convertToPromise(value) {
    return (0, isFunction_1.default)((0, get_1.default)(value, 'then')) ? value : Promise.resolve(value);
}
const sourceErrorTemplate = (converterName, parameter) => `${converterName} failed validation because you did not explicitly pass ${parameter} to the function returned by "connect"`;
exports.sourceErrorTemplate = sourceErrorTemplate;
exports.withComponent = createConverter({
    name: 'withComponent',
    test: (nameSource, { component }) => {
        if (!isVueComponent(component))
            return (0, exports.sourceErrorTemplate)('withComponent', 'the VueComponent instance');
        return true;
    },
    convert: (mapOfWatchers, { component }) => {
        const observers = [];
        component.$on('hook:beforeDestroy', () => {
            observers.forEach((observer) => observer.complete());
            observers.length = 0;
        });
        return (0, map_1.default)(mapOfWatchers, (watcher, name) => {
            return rxjs_1.Observable.create((observer) => {
                component.$watch(watcher, (value) => {
                    observer.next({ name, value });
                }, { immediate: true });
                observers.push(observer);
            });
        });
    }
});
/**
 * Similar to withComponent, but useful when a large number of aggregation values come from deep references on a single
 * Vue component property. This converter only produces a single Observable for the top-level property, avoiding the
 * "redundant request" console warning.
 *
 * For example, if withComponent is called like this:
 *
        const aggregation = connectAggregationToState(
            ProductEngagementScoreAggregation({ name }),
            withComponent({
                adoptionCoreEventList: 'aggParams.adoptionCoreEventList',
                appId: 'aggParams.appId',
                config: 'aggParams.config',
                filters: 'aggParams.filters',
                timeSeries: `aggParams.timeSeries.${timeSeriesKey}`,
                retry: 'aggParams.retry'
            })
        )({ component: this });
 *
 * Then withComponentParam can be used in this way:
 *
        const aggregation = connectAggregationToState(
            ProductEngagementScoreAggregation({ name }),
            withComponentParam({
                paramToWatch: 'aggParams',
                mapOfWatchers: {
                    adoptionCoreEventList: 'adoptionCoreEventList',
                    appId: 'appId',
                    config: 'config',
                    filters: 'filters',
                    timeSeries: `timeSeries.${timeSeriesKey}`,
                    retry: 'retry'
                }
            })
        )({ component: this });
 */
exports.withComponentParam = createConverter({
    name: 'withComponentParam',
    test: (_nameSource, { component }) => {
        if (!isVueComponent(component))
            return (0, exports.sourceErrorTemplate)('withComponentParam', 'the VueComponent instance');
        return true;
    },
    convert: ({ paramToWatch, mapOfWatchers }, { component }) => {
        let paramObserver;
        component.$on('hook:beforeDestroy', () => {
            paramObserver.complete();
        });
        return new rxjs_1.Observable((observer) => {
            component.$watch(paramToWatch, (value) => {
                const valueArray = (0, map_1.default)(mapOfWatchers, (path, name) => ({
                    value: (0, get_1.default)(value, path),
                    name
                }));
                observer.next(valueArray);
            }, { immediate: true });
            paramObserver = observer;
        });
    }
});
exports.withStore = createConverter({
    name: 'withStore',
    test: (nameSource, { component, store }) => {
        if (!isStore(store))
            return (0, exports.sourceErrorTemplate)('withStore', 'the (Vuex) Store instance');
        if (!isVueComponent(component))
            return (0, exports.sourceErrorTemplate)('withStore', 'the VueComponent instance');
        return true;
    },
    convert: (mapOfWatchers, { store, component }) => {
        const observers = [];
        component.$on('hook:beforeDestroy', () => {
            observers.forEach(([observer, unwatch]) => {
                observer.complete();
                unwatch();
            });
            observers.length = 0;
        });
        return (0, map_1.default)(mapOfWatchers, (watcher, name) => {
            return rxjs_1.Observable.create((observer) => {
                const unwatch = store.watch(watcher, (value) => {
                    observer.next({ name, value });
                }, { immediate: true });
                observers.push([observer, unwatch]);
            });
        });
    }
});
exports.withPromise = createConverter({
    name: 'withPromise',
    test: () => true,
    convert: (mapOfPromises) => {
        return (0, map_1.default)(mapOfPromises, (promise, name) => {
            promise = convertToPromise(promise);
            return rxjs_1.Observable.create((observer) => {
                promise.then((value) => {
                    observer.next({ name, value });
                    observer.complete();
                }, (error) => {
                    observer.error({ name, error });
                    observer.complete();
                });
            });
        });
    }
});
